// The index strip — the seven benchmarks across the top of every ticker page.
//
// Owner: "then be at the top as a secondary menu on all other pages."
//
// ⚠️ IT LIVES IN TickerShell, WHICH IS ONE INSERTION FOR A DOZEN PAGES. The
// homepage does NOT get it: it has the full rail down the right side, and a
// strip repeating the same seven numbers four inches above them would be the
// same figures twice on one screen.
//
// ⚠️ "ALL OTHER PAGES" IS NOT ALL PAGES ON THE HOST, AND THAT IS WORTH SAYING
// PLAINLY. TickerShell covers the ticker surface — diesel, receipts, toppers,
// the sale sheet, state tickers, fat cattle, the barn leaderboard, the index
// pages themselves. The raisedbeef.com front gate, the platform at
// markets.raisedbeef.ai, Clara, the consultant and the barn board each carry
// their own chrome and are NOT covered by this. Each is its own piece of work.
//
// ⚠️ NO HORIZONTAL SCROLLING, AT ANY WIDTH. That is an owner rule and the
// reason the strip is built the way it is: four indexes and a link on a phone,
// all seven on a wide screen, decided in CSS. The alternative everyone reaches
// for — one row that scrolls sideways — is the thing ruled out.

import { useEffect, useState } from "react";

import {
  type RbIndexLive,
  type RbIndexTile,
  fetchRbIndexLive,
  fetchRbIndexPublic,
  fetchRbIndexes,
} from "@/lib/rbIndexes";
import { useIndexAccess } from "@/lib/indexAccess";
import { useInApp } from "@/rbapp/inApp";

import LiveBeat from "./LiveBeat";
import LockMark from "./LockMark";
import { useFlash } from "./useFlash";

/** ⚠️ TWENTY SECONDS, AND THE FIRST MEASUREMENT OF THIS WAS WRONG. The gap
 *  between consecutive ROWS is ~3.8s, but that is rows inside one ingest
 *  batch and says nothing about how often new data appears. Measured properly
 *  on 2026-10-05: during a sale day 56-60 minutes out of every 60 receive new
 *  rows, and the median gap between minutes-with-new-data is exactly 60
 *  seconds — DV ingest is continuous once sales are running, and roughly
 *  7.5-minute batches before about 11:00Z. So new data exists every minute,
 *  and 20s polling against a 20s server cache keeps the worst-case lag at 40s
 *  — always inside the minute. Fifteen seconds would mostly redraw an
 *  identical payload; sixty could sit a whole batch behind. */
const LIVE_POLL_MS = 20_000;

/** ⚠️ THE HOMEPAGE'S NAMES, NOT SHORT ONES (owner, 2026-10-12: the strip's
 *  names "don't match the names on the home page"). Only "RB " is dropped, as
 *  on the homepage cards; long names wrap to a second line, never "…". */
const stripName = (t: RbIndexTile) => t.name.replace(/^(RB|DVA) /, "");

/** The homepage cards' window: what the market is doing lately. */
const SPARK_DAYS = 30;

function sparkPath(prices: number[], w: number, h: number): { line: string; area: string } | null {
  if (prices.length < 2) return null;
  const M = 2;
  const lo = Math.min(...prices);
  const span = Math.max(...prices) - lo || 1;
  const x = (i: number) => M + (i / (prices.length - 1)) * (w - M * 2);
  const y = (v: number) => h - M - ((v - lo) / span) * (h - M * 2);
  const line = prices.map((v, i) => `${i ? "L" : "M"}${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join(" ");
  return { line, area: `${line} L${(w - M).toFixed(1)} ${h} L${M} ${h} Z` };
}

export default function RbIndexStrip() {
  const [tiles, setTiles] = useState<RbIndexTile[]>([]);
  const [live, setLive] = useState<Record<string, RbIndexLive>>({});
  const [liveAt, setLiveAt] = useState<number | null>(null);
  // Premium lock, 2026-10-13: names and a padlock for everyone else (mock).
  const access = useIndexAccess();
  const open = access.state === "loading" ? null : access.state === "open";
  const inApp = useInApp();

  useEffect(() => {
    if (open === null) return;
    let on = true;
    // One fetch, no poll — a published index is written once a night and
    // cannot change until the next run. Same reasoning as the homepage rail.
    (open ? fetchRbIndexes() : fetchRbIndexPublic().then((p) => p.tiles))
      .then((rows) => { if (on && rows.length) setTiles(rows); })
      .catch(() => { /* the strip simply stays empty; it is navigation */ });
    return () => { on = false; };
  }, [open]);

  // ⚠️ THE LIVE PRICE POLLS; THE PUBLISHED ONE DOES NOT. Owner, relaying Rob:
  // "a live price throughout the day so people can watch it live from morning
  // till night" — a number that only moved on reload would not be that. Two
  // separate calls because the board is cached fifteen minutes and this one
  // twenty seconds; folding them together would force one of those to be wrong.
  useEffect(() => {
    if (open !== true) return;
    let on = true;
    const pull = () => {
      if (document.visibilityState === "hidden") return;
      fetchRbIndexLive()
        .then((m) => { if (on) { setLive(m); setLiveAt(Date.now()); } })
        .catch(() => { /* the live line just stays blank */ });
    };
    pull();
    const id = window.setInterval(pull, LIVE_POLL_MS);
    document.addEventListener("visibilitychange", pull);
    return () => {
      on = false;
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", pull);
    };
  }, [open]);
  const locked = tiles.some((t) => t.locked);

  // ⚠️ THE EMPTY STRIP STILL OCCUPIES ITS HEIGHT. Several of these pages are
  // PRERENDERED, so the figures arrive after hydration. Rendering nothing and
  // then dropping a bar in would shove the whole page down under the reader's
  // eye — the same reason PicksMenu reserves its space for the weekday.
  return (
    // ⚠️ data-locked SIZES THE RESERVATION. A locked tile (name + padlock) is
    // about half the height of a priced one, and the strip's min-height was
    // sized for priced tiles only — so every signed-out visitor got the tiles
    // and then a block of empty navy below them, 173 px of it on a phone
    // (owner, 2026-10-16: "What is this big gap when I click on the
    // leaderboard on my phone"). Set as soon as access resolves closed, before
    // the tiles arrive, so the reservation is right from the first paint.
    <nav className="rbtx-ixstrip" aria-label="Cattle indexes" data-empty={tiles.length === 0 || undefined} data-locked={open === false || locked || undefined}>
      <div className="rbtx-ixstrip-in">
        {tiles.map((t) => (
          <StripItem key={t.slug} tile={t} live={live[t.slug]} />
        ))}
        {tiles.length > 0 && !locked && (
          // ⚠️ /index, NOT A SPECIFIC INDEX. This pointed at
          // /index/rb_feeder_cattle, written before the /index landing page
          // existed — so on that index's own page the All button navigated to
          // the page you were already on and appeared to do NOTHING. On a
          // phone the strip shows two tiles, so landing on Feeder Cattle and
          // then tapping All is the ordinary path to the bug, which is how the
          // owner found it. /index lists all nineteen, which is what the word
          // says.
          <a className="rbtx-ixstrip-all" href="/index">
            <span>All indexes →</span>
          </a>
        )}
        {/* ⚠️ NOT IN THE APP: the app never sells (Apple's rule). */}
        {locked && !inApp && (
          <a className="rbtx-ixstrip-all" href="/membership">
            <span>See plans →</span>
          </a>
        )}
        {/* ⚠️ ONE BEAT FOR THE WHOLE BAR, NOT ONE PER INDEX. Six pulsing dots
            across the top of every page would be noise, and the thing being
            reported — when the feed last answered — is the same for all of
            them. Hidden below 860px, where the strip is already dropping
            indexes to avoid the horizontal scrolling the owner has ruled out. */}
        {tiles.length > 0 && !locked && <LiveBeat at={liveAt} />}
      </div>
    </nav>
  );
}

/**
 * One index on the strip.
 *
 * ⚠️ THE BIG NUMBER IS THE LIVE INDEX, NOT LAST NIGHT'S CLOSE. That is the
 * whole point of the change: owner, relaying Rob, "the actual index number is
 * changing" through the day. The close is still the fallback, because before
 * the first sale of the morning — and all weekend — there is nothing newer to
 * show, and a blank where a price belongs is worse than yesterday's price.
 *
 * ⚠️ TWO DIFFERENT NUMBERS, LABELLED DIFFERENTLY ON PURPOSE. The headline is
 * the seven-day index including today; the small row is TODAY alone. Measured
 * across all eighteen on 2026-10-05 they sit a median $6.53 apart and as much
 * as $22.65 on a thin index, because today-only can be built on a handful of
 * morning lots. Two live cattle prices disagreeing on one tile is a support
 * question unless it is obvious which is which, so the small one is prefixed
 * "Today".
 */
function StripItem({ tile, live }: { tile: RbIndexTile; live?: RbIndexLive }) {
  if (tile.locked) {
    return (
      <a className="rbtx-ixstrip-item" href={`/index/${tile.slug}`} data-locked="1">
        <span className="rbtx-ixstrip-k">{stripName(tile)}</span>
        <span className="rbtx-ixstrip-v"><LockMark /></span>
      </a>
    );
  }
  return <StripPrices tile={tile} live={live} />;
}

/** Split out so the hooks inside run on every render of a priced item. */
function StripPrices({ tile, live }: { tile: RbIndexTile; live?: RbIndexLive }) {
  // Flash on the numeric value, never the formatted string: "$342.30" and
  // "$342.3" are the same price and different text.
  const ixNum = live?.indexValue ? Number(live.indexValue.replace(/[$,]/g, "")) : null;
  const tdNum = live?.value ? Number(live.value.replace(/[$,]/g, "")) : null;
  const ixFlash = useFlash(ixNum);
  const tdFlash = useFlash(live ? live.headCount : null);

  const headline = live?.indexValue ?? tile.value;
  // The line ends on the number shown, as on the homepage cards.
  const recent = ixNum !== null && Number.isFinite(ixNum)
    ? [...tile.spark.slice(-(SPARK_DAYS - 1)), ixNum]
    : tile.spark.slice(-SPARK_DAYS);
  const sp = sparkPath(recent, 200, 22);
  const dir = (live?.indexValue ? live.dayDir : tile.dayDir) ?? "flat";
  const change = (live?.indexValue ? live.dayChange : tile.dayChange) ?? "—";

  return (
    <a className="rbtx-ixstrip-item" href={`/index/${tile.slug}`}>
      <span className="rbtx-ixstrip-k">{stripName(tile)}</span>
      <span className="rbtx-ixstrip-v" data-flash={ixFlash ?? undefined}>{headline}</span>
      {/* Signed, with an arrow only when it moved — the homepage's rule, so
          the two never disagree. Live move against yesterday's close where we
          have it. */}
      <span className="rbtx-ixstrip-c" data-dir={dir}>
        {change}{dir === "up" ? " ▲" : dir === "down" ? " ▼" : ""}
      </span>
      {sp ? (
        <svg className="rbtx-ixstrip-spark" data-dir={recent[recent.length - 1] >= recent[0] ? "up" : "down"}
             viewBox="0 0 200 22" preserveAspectRatio="none" aria-hidden="true">
          <path d={sp.area} fill="currentColor" fillOpacity="0.14" />
          <path d={sp.line} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
        </svg>
      ) : <span className="rbtx-ixstrip-spark" aria-hidden="true" />}
      {/* ⚠️ ALWAYS RENDERED, EVEN EMPTY. Nothing trades before the first sale
          of the morning, and on a Sunday nothing trades at all — but a line
          that appeared mid-session would shove every page down under the
          reader's eye, the same reason the strip holds its height while
          loading. Empty, it is just a reserved row.

          ⚠️ A ROW OF ITS OWN, NOT A SECOND FIGURE BESIDE THE HEADLINE. At
          480px each item gets about 81px and a price needs 80 of them, so a
          second number on that line is how you get the horizontal scrolling
          the owner has ruled out. Height is free; width is not. */}
      <span className="rbtx-ixstrip-l" data-on={live?.value ? "" : undefined}
            data-flash={tdFlash ?? undefined}>
        {live?.value ? (
          <>
            <i className="rbtx-ixstrip-dot" aria-hidden="true" />
            <em className="rbtx-ixstrip-tag">Today</em>
            <b>{live.value}</b>
            {/* ⚠️ THE DEPTH GOES WITH THE PRICE, ALWAYS. Only 3.7% of a sale
                day's lots are in by 8am and 22% by 10am, so an early figure is
                thin by nature. Print the head count or the row lies. */}
            <em>{live.head} hd</em>
          </>
        ) : null}
      </span>
    </a>
  );
}
